import React, { useState, useEffect, useMemo } from 'react';
import { 
  FileText, 
  Download, 
  Plus, 
  Search, 
  Filter, 
  Edit, 
  Trash2, 
  Eye, 
  DownloadCloud,
  RefreshCw,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import Card, { CardHeader, CardContent } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Select from '../components/ui/Select';
import { useAppStore } from '../store';
import { formatArgentinaDate } from '../utils/dateUtils';
import { generateDeliveryNotePDF } from '../utils/pdfGenerator';
import DeliveryNoteModal from '../components/modals/DeliveryNoteModal';
import DeleteConfirmModal from '../components/modals/DeleteConfirmModal';
import { DeliveryNote } from '../types';

interface FilterState {
  searchQuery: string;
  clientId: string;
  dateFrom: string;
  dateTo: string;
  carrierName: string;
  sortBy: 'date' | 'number' | 'clientName' | 'carrierName';
  sortOrder: 'asc' | 'desc';
}

const Remitos: React.FC = () => {
  const { 
    clients, 
    deliveryNotes, 
    fetchDeliveryNotes, 
    addDeliveryNote, 
    updateDeliveryNote, 
    deleteDeliveryNote 
  } = useAppStore();
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingNote, setEditingNote] = useState<DeliveryNote | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{ isOpen: boolean; note: DeliveryNote | null }>({
    isOpen: false,
    note: null
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  
  const [filters, setFilters] = useState<FilterState>({
    searchQuery: '',
    clientId: 'all',
    dateFrom: '',
    dateTo: '',
    carrierName: '',
    sortBy: 'date',
    sortOrder: 'desc'
  });
  
  // Fetch delivery notes from database on component mount
  useEffect(() => {
    const loadDeliveryNotes = async () => {
      try {
        setIsLoading(true);
        
        // First, try to fetch from database
        await fetchDeliveryNotes();
        
        // If database is empty but localStorage has data, migrate it
        if (deliveryNotes.length === 0) {
          const storedNotes = localStorage.getItem('deliveryNotes');
          if (storedNotes) {
            try {
              const localNotes = JSON.parse(storedNotes);
              console.log('[REMITOS] Found localStorage data, migrating to database:', localNotes.length, 'notes');
              
              // Migrate each note to database
              for (const note of localNotes) {
                const dbNote = {
                  number: note.number,
                  date: note.date,
                  client_id: note.clientId,
                  client_name: note.clientName,
                  client_address: note.clientAddress || '',
                  client_city: note.clientCity || '',
                  client_province: note.clientProvince || '',
                  client_cuit: note.clientCuit || '',
                  carrier_name: note.carrierName || '',
                  delivered_by: note.deliveredBy || '',
                  items: note.items || [],
                };
                
                await addDeliveryNote(dbNote);
              }
              
              // Clear localStorage after successful migration
              localStorage.removeItem('deliveryNotes');
              console.log('[REMITOS] Successfully migrated localStorage data to database');
              
              // Refresh the data from database
              await fetchDeliveryNotes();
              
            } catch (migrationError) {
              console.error('[REMITOS] Error migrating localStorage data:', migrationError);
              // Continue with database data even if migration fails
            }
          }
        }
        
      } catch (error) {
        console.error('Error fetching delivery notes:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadDeliveryNotes();
  }, [fetchDeliveryNotes, addDeliveryNote, deliveryNotes.length]);
  
  // Filter and sort delivery notes
  const filteredAndSortedNotes = useMemo(() => {
    let filtered = deliveryNotes.filter(note => {
      // Search query filter
      if (filters.searchQuery) {
        const query = filters.searchQuery.toLowerCase();
        if (!note.number.toLowerCase().includes(query) &&
            !note.client_name.toLowerCase().includes(query) &&
            !note.carrier_name.toLowerCase().includes(query)) {
          return false;
        }
      }
      
      // Client filter
      if (filters.clientId !== 'all' && note.clientId !== filters.clientId) {
        return false;
      }
      
      // Date range filter
      if (filters.dateFrom && note.date < filters.dateFrom) {
        return false;
      }
      if (filters.dateTo && note.date > filters.dateTo) {
        return false;
      }
      
      // Carrier filter
      if (filters.carrierName && !note.carrier_name.toLowerCase().includes(filters.carrierName.toLowerCase())) {
        return false;
      }
      
      return true;
    });
    
    // Sort notes
    filtered.sort((a, b) => {
      let aValue: any, bValue: any;
      
      switch (filters.sortBy) {
        case 'date':
          aValue = new Date(a.date);
          bValue = new Date(b.date);
          break;
        case 'number':
          aValue = a.number;
          bValue = b.number;
          break;
        case 'clientName':
          aValue = a.client_name;
          bValue = b.client_name;
          break;
        case 'carrierName':
          aValue = a.carrier_name;
          bValue = b.carrier_name;
          break;
        default:
          aValue = new Date(a.date);
          bValue = new Date(b.date);
      }
      
      if (filters.sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
    
    return filtered;
  }, [deliveryNotes, filters]);
  
  // Pagination
  const totalPages = Math.ceil(filteredAndSortedNotes.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedNotes = filteredAndSortedNotes.slice(startIndex, endIndex);
  
  
  // Handle saving a new delivery note
  const handleSaveDeliveryNote = async (deliveryNote: any) => {
    console.log('[REMITOS] handleSaveDeliveryNote called with:', deliveryNote);
    console.log('[REMITOS] Current deliveryNotes count:', deliveryNotes.length);
    console.log('[REMITOS] Editing note:', editingNote);
    
    try {
      if (editingNote) {
        // Update existing note
        console.log('[REMITOS] Updating existing note:', editingNote.id);
        await updateDeliveryNote(editingNote.id, deliveryNote);
        setEditingNote(null);
      } else {
        // Add new note
        console.log('[REMITOS] Adding new note');
        await addDeliveryNote(deliveryNote);
      }
      
      console.log('[REMITOS] Successfully saved delivery note');
      setIsModalOpen(false);
    } catch (error) {
      console.error('[REMITOS] Error saving delivery note:', error);
      // Error will be handled by the store and shown in ErrorModal
    }
  };
  
  // Handle editing a delivery note
  const handleEditNote = (note: DeliveryNote) => {
    setEditingNote(note);
    setIsModalOpen(true);
  };
  
  // Handle deleting a delivery note
  const handleDeleteNote = (note: DeliveryNote) => {
    setDeleteConfirm({ isOpen: true, note });
  };
  
  // Confirm delete
  const confirmDelete = async () => {
    if (deleteConfirm.note) {
      try {
        console.log('[REMITOS] Deleting delivery note:', deleteConfirm.note.id);
        await deleteDeliveryNote(deleteConfirm.note.id);
        console.log('[REMITOS] Successfully deleted delivery note');
        setDeleteConfirm({ isOpen: false, note: null });
      } catch (error) {
        console.error('[REMITOS] Error deleting delivery note:', error);
        // Error will be handled by the store and shown in ErrorModal
      }
    }
  };
  
  // Generate PDF for a delivery note
  const handleGeneratePDF = (deliveryNote: DeliveryNote) => {
    generateDeliveryNotePDF(deliveryNote);
  };
  
  // Generate PDFs for multiple notes
  const handleBulkDownload = () => {
    paginatedNotes.forEach(note => {
      setTimeout(() => generateDeliveryNotePDF(note), 100);
    });
  };
  
  // Reset filters
  const resetFilters = () => {
    setFilters({
      searchQuery: '',
      clientId: 'all',
      dateFrom: '',
      dateTo: '',
      carrierName: '',
      sortBy: 'date',
      sortOrder: 'desc'
    });
    setCurrentPage(1);
  };
  
  // Close modal and reset editing state
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingNote(null);
  };
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white flex items-center">
          <FileText className="mr-2 h-6 w-6 text-indigo-600 dark:text-indigo-400" />
          Remitos
        </h2>
        <div className="flex space-x-2">
          <Button
            variant="outline"
            icon={<Filter className="h-5 w-5" />}
            onClick={() => setShowFilters(!showFilters)}
          >
            Filtros
          </Button>
          <Button
            variant="primary"
            icon={<Plus className="h-5 w-5" />}
            onClick={() => setIsModalOpen(true)}
          >
            Nuevo Remito
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              console.log('[REMITOS DEBUG] Current state:', {
                deliveryNotes: deliveryNotes.length,
                localStorage: localStorage.getItem('deliveryNotes'),
                filteredNotes: filteredAndSortedNotes.length
              });
            }}
            className="text-xs"
          >
            Debug
          </Button>
        </div>
      </div>
      
      
      {/* Filters */}
      {showFilters && (
        <Card>
          <CardHeader title="Filtros y Búsqueda" />
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="relative">
                <Input
                  label="Buscar"
                  id="searchQuery"
                  name="searchQuery"
                  value={filters.searchQuery}
                  onChange={(e) => setFilters({ ...filters, searchQuery: e.target.value })}
                  placeholder="Número, cliente o transportista..."
                />
                <div className="absolute right-3 top-9">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
              </div>
              
              <Select
                label="Cliente"
                value={filters.clientId}
                onChange={(value) => setFilters({ ...filters, clientId: value })}
                options={[
                  { value: 'all', label: 'Todos los clientes' },
                  ...clients.map(client => ({
                    value: client.id,
                    label: client.name,
                  }))
                ]}
              />
              
              <Input
                type="date"
                label="Fecha desde"
                id="dateFrom"
                name="dateFrom"
                value={filters.dateFrom}
                onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
              />
              
              <Input
                type="date"
                label="Fecha hasta"
                id="dateTo"
                name="dateTo"
                value={filters.dateTo}
                onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
              />
              
              <Input
                label="Transportista"
                id="carrierName"
                name="carrierName"
                value={filters.carrierName}
                onChange={(e) => setFilters({ ...filters, carrierName: e.target.value })}
                placeholder="Nombre del transportista"
              />
              
              <div className="flex space-x-2">
                <Select
                  label="Ordenar por"
                  value={filters.sortBy}
                  onChange={(value) => setFilters({ ...filters, sortBy: value as any })}
                  options={[
                    { value: 'date', label: 'Fecha' },
                    { value: 'number', label: 'Número' },
                    { value: 'clientName', label: 'Cliente' },
                    { value: 'carrierName', label: 'Transportista' }
                  ]}
                />
                <Button
                  variant="outline"
                  onClick={() => setFilters({ ...filters, sortOrder: filters.sortOrder === 'asc' ? 'desc' : 'asc' })}
                >
                  {filters.sortOrder === 'asc' ? '↑' : '↓'}
                </Button>
              </div>
            </div>
            
            <div className="flex justify-end space-x-2 mt-4">
              <Button
                variant="outline"
                onClick={resetFilters}
              >
                Limpiar Filtros
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Delivery Notes List */}
      <Card>
        <CardHeader 
          title={`Remitos (${filteredAndSortedNotes.length})`}
          action={
            <div className="flex space-x-2">
              <Button
                variant="outline"
                size="sm"
                icon={<DownloadCloud className="h-4 w-4" />}
                onClick={handleBulkDownload}
                disabled={paginatedNotes.length === 0}
              >
                Descargar Todos
              </Button>
              <Button
                variant="outline"
                size="sm"
                icon={<RefreshCw className="h-4 w-4" />}
                onClick={() => window.location.reload()}
              >
                Actualizar
              </Button>
            </div>
          }
        />
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 dark:border-indigo-400"></div>
            </div>
          ) : filteredAndSortedNotes.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              {filters.searchQuery || filters.clientId !== 'all' || filters.dateFrom || filters.dateTo || filters.carrierName
                ? 'No se encontraron remitos con los criterios de búsqueda'
                : 'No hay remitos registrados'
              }
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Número
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Fecha
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Cliente
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Transportista
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Items
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                    {paginatedNotes.map((note) => (
                      <tr key={note.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                          {note.number}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {formatArgentinaDate(note.date)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {note.client_name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {note.carrier_name || 'No especificado'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {note.items.length} item{note.items.length !== 1 ? 's' : ''}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                          <div className="flex justify-end space-x-2">
                            <Button
                              variant="outline"
                              size="sm"
                              icon={<Eye className="h-4 w-4" />}
                              onClick={() => handleGeneratePDF(note)}
                              title="Ver PDF"
                            />
                            <Button
                              variant="outline"
                              size="sm"
                              icon={<Edit className="h-4 w-4" />}
                              onClick={() => handleEditNote(note)}
                              title="Editar"
                            />
                            <Button
                              variant="outline"
                              size="sm"
                              icon={<Download className="h-4 w-4" />}
                              onClick={() => handleGeneratePDF(note)}
                              title="Descargar PDF"
                            />
                            <Button
                              variant="outline"
                              size="sm"
                              icon={<Trash2 className="h-4 w-4" />}
                              onClick={() => handleDeleteNote(note)}
                              title="Eliminar"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300"
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <div className="text-sm text-gray-700 dark:text-gray-300">
                    Mostrando {startIndex + 1} a {Math.min(endIndex, filteredAndSortedNotes.length)} de {filteredAndSortedNotes.length} resultados
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      icon={<ChevronLeft className="h-4 w-4" />}
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                    >
                      Anterior
                    </Button>
                    <div className="flex space-x-1">
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                        <Button
                          key={page}
                          variant={currentPage === page ? "primary" : "outline"}
                          size="sm"
                          onClick={() => setCurrentPage(page)}
                        >
                          {page}
                        </Button>
                      ))}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      icon={<ChevronRight className="h-4 w-4" />}
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                    >
                      Siguiente
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
      
      {/* Delivery Note Modal */}
      <DeliveryNoteModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveDeliveryNote}
        clients={clients}
        editingNote={editingNote}
      />
      
      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={deleteConfirm.isOpen}
        onClose={() => setDeleteConfirm({ isOpen: false, note: null })}
        onConfirm={confirmDelete}
        title="Eliminar Remito"
        message={`¿Estás seguro de que quieres eliminar el remito ${deleteConfirm.note?.number}? Esta acción no se puede deshacer.`}
      />
    </div>
  );
};

export default Remitos;